import { useRef, useState } from "react";
import { Box, Divider, MenuItem, MenuList, Popover, Typography } from "@mui/material";
import { DotLegalEmptyState, DotLegalHeader, DotLegalSearchField, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { EntityTemplateStandardField, getStandardFieldTranslationKey, SelectableInputField } from "./EntityTemplate.types.ts";
import { InputFieldTypeEnum } from "./inputFields/InputFields.types.ts";
import { useTranslation } from "../localization/useTranslation.ts";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "../common/stringOperations.ts";

interface AddFieldPopoverProps {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    availableStandardFields: EntityTemplateStandardField[];
    availableCustomFields: SelectableInputField[];
    onFieldAdd: (standardField?: EntityTemplateStandardField, customField?: SelectableInputField) => void;
}

function AddFieldPopover(props: AddFieldPopoverProps) {
    const { translateString } = useTranslation();
    const [searchText, setSearchText] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);
    const menuListRef = useRef<HTMLUListElement>(null);

    const handleClose = () => {
        setSearchText("");
        props.onClose();
    };

    const handleFieldSelect = (standardField?: EntityTemplateStandardField, customField?: SelectableInputField) => {
        setSearchText("");
        props.onFieldAdd(standardField, customField);
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            const firstItem = menuListRef.current?.querySelector("[role='menuitem']") as HTMLElement | null;
            firstItem?.focus();
        }
    };

    const filteredStandardFields = props.availableStandardFields.filter((field) =>
        translateString(getStandardFieldTranslationKey(field)).toLowerCase().includes(searchText.toLowerCase())
    );

    const filteredCustomFields = props.availableCustomFields.filter((customField) => customField.name.toLowerCase().includes(searchText.toLowerCase()));

    return (
        <Popover
            anchorEl={props.anchorEl}
            open={Boolean(props.anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
                paper: {
                    sx: {
                        width: 300,
                        height: 450,
                        display: "flex",
                        flexDirection: "column",
                    },
                },
            }}
        >
            <Box sx={{ padding: 1, borderBottom: 1, borderColor: "divider" }} onKeyDown={handleSearchKeyDown}>
                <DotLegalSearchField
                    labelSearch={translateString("search")}
                    value={searchText}
                    debounce={false}
                    autoFocus
                    inputRef={searchInputRef}
                    onChange={(value) => setSearchText(value)}
                    useFullWidth
                />
            </Box>

            <Box sx={{ overflowY: "auto", flexGrow: 1, scrollbarWidth: "thin" }}>
                {!isNullOrWhitespace(searchText) && filteredStandardFields.length === 0 && filteredCustomFields.length === 0 ? (
                    <Box sx={{ height: "100%" }}>
                        <DotLegalEmptyState text={translateString("noResultsTryNewSearchWord")} noFixedHeight />
                    </Box>
                ) : (
                    <MenuList ref={menuListRef} autoFocusItem={false}>
                        {filteredStandardFields.length > 0 && (
                            <>
                                <DotLegalTooltip position="right" text={translateString("standardFieldMeaningTooltip")}>
                                    <Box component={"span"}>
                                        <MenuItem disabled sx={{ "&.Mui-disabled": { opacity: 1 } }}>
                                            <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                                {translateString("standardFields")}
                                            </DotLegalHeader>
                                        </MenuItem>
                                    </Box>
                                </DotLegalTooltip>
                                <Divider sx={{ my: 1 }} />
                                {filteredStandardFields.map((field) => (
                                    <MenuItem key={field} onClick={() => handleFieldSelect(field)}>
                                        {translateString(getStandardFieldTranslationKey(field))}
                                    </MenuItem>
                                ))}
                            </>
                        )}

                        {filteredCustomFields.length > 0 && (
                            <>
                                {filteredStandardFields.length > 0 && <Divider sx={{ my: 1 }} />}

                                <DotLegalTooltip position="right" text={translateString("customFieldMeaningTooltip")}>
                                    <Box component={"span"}>
                                        <MenuItem disabled sx={{ "&.Mui-disabled": { opacity: 1 } }}>
                                            <DotLegalHeader marginBottom={0} headerStyle={"small"}>
                                                {translateString("customFields")}
                                            </DotLegalHeader>
                                        </MenuItem>
                                    </Box>
                                </DotLegalTooltip>
                                <Divider sx={{ my: 1 }} />
                                {filteredCustomFields.map((customField) => (
                                    <MenuItem key={customField.id} onClick={() => handleFieldSelect(undefined, customField)}>
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            {customField.name}
                                            <Typography sx={(theme) => ({ color: theme.palette.grey[600], fontSize: 10 })}>
                                                {translateString(setFirstLetterToLowerCase(InputFieldTypeEnum[customField.type].toString()))}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </>
                        )}
                    </MenuList>
                )}
            </Box>
        </Popover>
    );
}

export default AddFieldPopover;
